import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const OVERRIDE_KEY = 'yachangu_country_override';

// ISO-3166 alpha-2 → alpha-3 for our markets
const ISO2_TO_ISO3 = { MW: 'MWI', TZ: 'TZA', ZM: 'ZMB', KE: 'KEN', ZW: 'ZWE' };

const FALLBACK_COUNTRIES = [
  { code: 'MWI', name: 'Malawi', flag: '🇲🇼', calling_code: '+265', currency: 'MWK', symbol: 'MK', supported: true, pawapay: true, cash_only: false, rate_from_mwk: 1, providers: [{ id: 'AIRTEL_MWI', label: 'Airtel Money', decimals: 2 }, { id: 'TNM_MWI', label: 'TNM Mpamba', decimals: 2 }] },
  { code: 'TZA', name: 'Tanzania', flag: '🇹🇿', calling_code: '+255', currency: 'TZS', symbol: 'TSh', supported: true, pawapay: true, cash_only: false, rate_from_mwk: 1.46, providers: [{ id: 'VODACOM_TZA', label: 'Vodacom M-Pesa', decimals: 0 }, { id: 'AIRTEL_TZA', label: 'Airtel Money', decimals: 2 }, { id: 'TIGO_TZA', label: 'Mixx by Yas (Tigo)', decimals: 0 }, { id: 'HALOTEL_TZA', label: 'Halotel', decimals: 0 }] },
  { code: 'ZMB', name: 'Zambia', flag: '🇿🇲', calling_code: '+260', currency: 'ZMW', symbol: 'ZK', supported: true, pawapay: true, cash_only: false, rate_from_mwk: 0.0155, providers: [{ id: 'AIRTEL_OAPI_ZMB', label: 'Airtel Money', decimals: 2 }, { id: 'MTN_MOMO_ZMB', label: 'MTN MoMo', decimals: 2 }, { id: 'ZAMTEL_ZMB', label: 'Zamtel Kwacha', decimals: 2 }] },
  { code: 'KEN', name: 'Kenya', flag: '🇰🇪', calling_code: '+254', currency: 'KES', symbol: 'KSh', supported: true, pawapay: true, cash_only: false, rate_from_mwk: 0.074, providers: [{ id: 'MPESA_KEN', label: 'M-Pesa', decimals: 0 }] },
  { code: 'ZWE', name: 'Zimbabwe', flag: '🇿🇼', calling_code: '+263', currency: 'USD', symbol: '$', supported: true, pawapay: false, cash_only: true, rate_from_mwk: 0.00058, providers: [] },
];

const CountryContext = createContext(null);
export const useCountry = () => useContext(CountryContext);

// Live vs preview: production domain OR a manual test toggle
export const isLiveDeployment = () => {
  try {
    if (localStorage.getItem('yachangu_force_live') === '1') return true;
    const h = window.location.hostname || '';
    return h.includes('yachangu.com');
  } catch {
    return false;
  }
};

async function detectIso3() {
  // 1) manual override
  try {
    const o = localStorage.getItem(OVERRIDE_KEY);
    if (o) return o;
  } catch { /* ignore */ }
  // 2) IP geolocation (no key, best-effort)
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 3500);
    const res = await fetch('https://ipapi.co/json/', { signal: ctrl.signal });
    clearTimeout(t);
    if (res.ok) {
      const j = await res.json();
      if (j.country_calling_code || j.country_code) {
        const iso3 = j.country_code_iso3 || ISO2_TO_ISO3[j.country_code] || null;
        if (iso3) return iso3;
        if (j.country_code) return j.country_code; // unsupported region → keep raw
      }
    }
  } catch { /* ignore */ }
  // 3) browser locale region
  try {
    const lang = navigator.language || '';
    const region = lang.split('-')[1];
    if (region && ISO2_TO_ISO3[region.toUpperCase()]) return ISO2_TO_ISO3[region.toUpperCase()];
  } catch { /* ignore */ }
  // 4) default to primary market
  return 'MWI';
}

export const CountryProvider = ({ children }) => {
  const [countries, setCountries] = useState(FALLBACK_COUNTRIES);
  const [detectedIso3, setDetectedIso3] = useState(null);
  const [loading, setLoading] = useState(true);
  const overrideRef = useRef(false);

  const setCountryOverride = (iso3) => {
    overrideRef.current = true;
    try { localStorage.setItem(OVERRIDE_KEY, iso3); } catch { /* ignore */ }
    setDetectedIso3(iso3);
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/countries/config`);
        const data = await res.json();
        if (data?.success && data.countries?.length) setCountries(data.countries);
      } catch { /* keep fallback */ }
      const iso3 = await detectIso3();
      // A manual pick made while detection was in flight always wins
      if (!overrideRef.current) setDetectedIso3(iso3);
      setLoading(false);
    })();
  }, []);

  const value = useMemo(() => {
    const byCode = Object.fromEntries(countries.map((c) => [c.code, c]));
    const active = (detectedIso3 && byCode[detectedIso3]) || null;
    const supported = !!active && active.supported;

    const symbol = active ? active.symbol : '$';
    const currency = active ? active.currency : 'USD';
    const rate = active ? (active.rate_from_mwk || 1) : 1;

    // Convert an MWK base amount into the active country's currency for display
    const convertFromBase = (mwkAmount) => Number(mwkAmount || 0) * rate;
    const formatMoney = (mwkAmount, { converted = false } = {}) => {
      const amt = converted ? Number(mwkAmount || 0) : convertFromBase(mwkAmount);
      const rounded = currency === 'USD' ? amt.toFixed(2) : Math.round(amt).toLocaleString();
      return `${symbol} ${rounded}`;
    };

    return {
      loading,
      countries,
      detectedIso3,
      country: active,
      supported,
      isSupportedCountry: supported,
      cashOnly: active ? active.cash_only : true,
      callingCode: active ? active.calling_code : '+',
      currency,
      symbol,
      providers: active ? (active.providers || []) : [],
      supportedCountries: countries.filter((c) => c.supported),
      setCountryOverride,
      convertFromBase,
      formatMoney,
      isLive: isLiveDeployment(),
    };
  }, [countries, detectedIso3, loading]);

  return <CountryContext.Provider value={value}>{children}</CountryContext.Provider>;
};

export default CountryContext;
